import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  clearInterviewDeferredMappingOpen,
  hideMappingLoadingOverlay,
  INTERVIEW_MAPPING_EDITOR_FALLBACK_MS,
  INTERVIEW_MAPPING_MIN_READ_MS,
  INTERVIEW_MAPPING_STATS_WAIT_MS,
} from '../utils/mappingLoadingOverlay';

type UseInterviewMappingRevealOptions = {
  interviewDeferredOpen: boolean;
  sId: string | undefined;
  /** Temps estimé process (messages overlay) — 0 tant que non chargé. */
  time: number;
  editorBlocked: boolean;
  /** Appelé quand l’overlay interview est retiré — repasse en mode carto normal. */
  onRevealComplete?: () => void;
};

function isSwimlaneCanvasPainted(): boolean {
  if (typeof document === 'undefined') return false;
  const root = document.getElementById('swimlane-container');
  if (!root) return false;
  return Boolean(
    root.querySelector('svg') ||
      root.querySelector('[class*="block-container"]') ||
      root.querySelector('.swimlane-header'),
  );
}

function waitForPaintedCanvas(maxAttempts = 120): Promise<boolean> {
  return new Promise((resolve) => {
    let attempts = 0;
    const tick = () => {
      if (isSwimlaneCanvasPainted()) {
        resolve(true);
        return;
      }
      attempts += 1;
      if (attempts >= maxAttempts) {
        resolve(false);
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

function waitFrames(count: number): Promise<void> {
  return new Promise((resolve) => {
    let remaining = count;
    const tick = () => {
      remaining -= 1;
      if (remaining <= 0) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

/**
 * Overlay interview → carto sans flash :
 * 1. SwimlaneError stable (animation + messages)
 * 2. Éditeur peint sous l'overlay (fixed, invisible pour l'utilisateur)
 * 3. Fond blanc + canvas peint sous l'overlay
 * 4. Retrait overlay — carto déjà là
 * 5. Relâchement du shell fixed après 2 frames
 */
export function useInterviewMappingReveal({
  interviewDeferredOpen,
  sId,
  time,
  editorBlocked,
  onRevealComplete,
}: UseInterviewMappingRevealOptions) {
  const onRevealCompleteRef = useRef(onRevealComplete);
  onRevealCompleteRef.current = onRevealComplete;
  const interviewDeferredOpenRef = useRef(interviewDeferredOpen);
  interviewDeferredOpenRef.current = interviewDeferredOpen;

  const interviewSheetReadyRef = useRef(false);
  const interviewEditorPaintedRef = useRef(false);
  const editorPaintRequiredRef = useRef(false);
  const revealingRef = useRef(false);
  const interviewLoadingStartedAtRef = useRef<number | null>(
    interviewDeferredOpen ? Date.now() : null,
  );
  const interviewRevealTimerRef = useRef<number | null>(null);
  const editorMountFrameRef = useRef<number | null>(null);
  const editorFallbackTimerRef = useRef<number | null>(null);

  const [spinner, setSpinner] = useState(interviewDeferredOpen);
  const [sheetReady, setSheetReady] = useState(false);
  const [mountEditorDom, setMountEditorDom] = useState(false);
  /** Éditeur rendu sous l'overlay (prêt à être révélé). */
  const [editorUnderlayReady, setEditorUnderlayReady] = useState(false);
  /** Overlay retiré — carto visible. */
  const [overlayDismissed, setOverlayDismissed] = useState(false);
  /** Fond blanc page (avant retrait overlay). */
  const [pageLight, setPageLight] = useState(false);
  /** Shell fixed relâché après révélation peinte. */
  const [editorShellReleased, setEditorShellReleased] = useState(false);

  const cancelEditorMount = useCallback(() => {
    if (editorMountFrameRef.current == null) return;
    window.cancelAnimationFrame(editorMountFrameRef.current);
    editorMountFrameRef.current = null;
  }, []);

  const cancelEditorFallback = useCallback(() => {
    if (editorFallbackTimerRef.current == null) return;
    window.clearTimeout(editorFallbackTimerRef.current);
    editorFallbackTimerRef.current = null;
  }, []);

  const scheduleEditorMount = useCallback(() => {
    cancelEditorMount();
    editorMountFrameRef.current = window.requestAnimationFrame(() => {
      editorMountFrameRef.current = window.requestAnimationFrame(() => {
        editorMountFrameRef.current = null;
        if (!interviewDeferredOpenRef.current) return;
        setMountEditorDom(true);
      });
    });
  }, [cancelEditorMount]);

  const reveal = useCallback(() => {
    if (revealingRef.current || !interviewDeferredOpenRef.current) return;
    revealingRef.current = true;
    cancelEditorFallback();

    setEditorUnderlayReady(true);

    void (async () => {
      await waitFrames(2);
      await waitForPaintedCanvas();

      await waitFrames(2);

      setPageLight(true);

      await waitFrames(1);

      interviewDeferredOpenRef.current = false;
      setSpinner(false);
      setSheetReady(false);
      setOverlayDismissed(true);
      hideMappingLoadingOverlay();
      clearInterviewDeferredMappingOpen();
      onRevealCompleteRef.current?.();

      await waitFrames(2);
      setEditorShellReleased(true);
      revealingRef.current = false;
    })();
  }, [cancelEditorFallback]);

  const scheduleReveal = useCallback(() => {
    if (!interviewDeferredOpenRef.current || !interviewSheetReadyRef.current) return;
    if (revealingRef.current) return;

    const started = interviewLoadingStartedAtRef.current ?? Date.now();
    const elapsed = Date.now() - started;
    const statsReady = time !== 0;
    const minReadDone = elapsed >= INTERVIEW_MAPPING_MIN_READ_MS;
    const statsWaitDone = statsReady || elapsed >= INTERVIEW_MAPPING_STATS_WAIT_MS;
    const editorReady =
      !editorPaintRequiredRef.current || interviewEditorPaintedRef.current;

    if (minReadDone && statsWaitDone && editorReady) {
      if (interviewRevealTimerRef.current != null) {
        window.clearTimeout(interviewRevealTimerRef.current);
        interviewRevealTimerRef.current = null;
      }
      reveal();
      return;
    }

    if (minReadDone && statsWaitDone && !editorReady) {
      if (editorFallbackTimerRef.current == null) {
        editorFallbackTimerRef.current = window.setTimeout(() => {
          editorFallbackTimerRef.current = null;
          interviewEditorPaintedRef.current = true;
          scheduleReveal();
        }, INTERVIEW_MAPPING_EDITOR_FALLBACK_MS);
      }
      return;
    }

    if (interviewRevealTimerRef.current != null) return;

    const wait = Math.max(
      minReadDone ? 0 : INTERVIEW_MAPPING_MIN_READ_MS - elapsed,
      statsWaitDone ? 0 : INTERVIEW_MAPPING_STATS_WAIT_MS - elapsed,
    );

    interviewRevealTimerRef.current = window.setTimeout(() => {
      interviewRevealTimerRef.current = null;
      scheduleReveal();
    }, wait);
  }, [reveal, time]);

  const onEditorInitialRender = useCallback(() => {
    if (!interviewDeferredOpenRef.current) return;
    interviewEditorPaintedRef.current = true;
    cancelEditorFallback();
    scheduleReveal();
  }, [scheduleReveal, cancelEditorFallback]);

  const finishOpenDisplay = useCallback(() => {
    if (!interviewDeferredOpenRef.current) {
      setSpinner(false);
      hideMappingLoadingOverlay();
      return;
    }
    interviewSheetReadyRef.current = true;
    setSheetReady(true);
    scheduleEditorMount();
    scheduleReveal();
  }, [scheduleEditorMount, scheduleReveal]);

  useLayoutEffect(() => {
    if (!interviewDeferredOpen) return;
    interviewSheetReadyRef.current = false;
    interviewEditorPaintedRef.current = false;
    revealingRef.current = false;
    cancelEditorMount();
    cancelEditorFallback();
    setSheetReady(false);
    setMountEditorDom(false);
    setEditorUnderlayReady(false);
    setOverlayDismissed(false);
    setPageLight(false);
    setEditorShellReleased(false);
    setSpinner(true);
    interviewLoadingStartedAtRef.current = Date.now();
  }, [interviewDeferredOpen, sId, cancelEditorMount, cancelEditorFallback]);

  const showLoadingOverlay = spinner && !overlayDismissed;

  /** Passe de l'overlay body (navigation) à l'overlay React une fois peint. */
  useEffect(() => {
    if (!showLoadingOverlay) return;

    let innerFrame = 0;
    const outerFrame = window.requestAnimationFrame(() => {
      innerFrame = window.requestAnimationFrame(() => {
        hideMappingLoadingOverlay();
      });
    });

    return () => {
      window.cancelAnimationFrame(outerFrame);
      if (innerFrame) window.cancelAnimationFrame(innerFrame);
    };
  }, [showLoadingOverlay]);

  useEffect(() => {
    if (interviewDeferredOpen && interviewSheetReadyRef.current) {
      scheduleReveal();
    }
  }, [time, interviewDeferredOpen, scheduleReveal]);

  useEffect(() => {
    return () => {
      if (interviewRevealTimerRef.current != null) {
        window.clearTimeout(interviewRevealTimerRef.current);
      }
      cancelEditorMount();
      cancelEditorFallback();
    };
  }, [cancelEditorMount, cancelEditorFallback]);

  const interviewLoading = spinner;
  editorPaintRequiredRef.current = sheetReady && !editorBlocked;
  const shouldMountEditor =
    !editorBlocked && (!interviewLoading || mountEditorDom);

  const keepEditorFixedShell = mountEditorDom && !editorShellReleased;

  const editorShellStyle = keepEditorFixedShell
    ? {
        position: 'fixed' as const,
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        visibility: (editorUnderlayReady ? 'visible' : 'hidden') as 'visible' | 'hidden',
        pointerEvents: 'none' as const,
        backgroundColor: pageLight ? '#ffffff' : '#212529',
      }
    : undefined;

  return {
    interviewLoading,
    shouldMountEditor,
    showLoadingOverlay,
    overlayZIndex: 1050,
    onEditorInitialRender: interviewDeferredOpen ? onEditorInitialRender : undefined,
    finishOpenDisplay,
    pageClassName: pageLight ? 'bg-white' : 'bg-dark',
    editorShellStyle,
  };
}
